import { Box, Typography } from '@mui/material';
import { useCallback } from 'react';
import { Dish, OrderItem } from '../../../apiTypes';
import Image from '../../../components/Image';
import OrderItemStatus from '../../../components/OrderItemStatus';
import { t } from '../../../constants/translations';
import { useLocale } from '../../../contexts/locale.context';
import { Map } from '../../../utils/map';

type Props = {
  item: OrderItem;
  dishesMap: Map<DbDocument<Dish>>;
  rightContent?: JSX.Element;
  noWrap?: boolean;
  onClick?: (item: OrderItem) => void;
  hideStatus?: boolean;
};

const OrderItemRow = ({ item, rightContent, dishesMap, noWrap, onClick, hideStatus }: Props) => {
  const { lang } = useLocale();

  const dish = dishesMap[item.dishId];

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(item);
    }
  }, [item, onClick]);

  return (
    <Box
      key={item.id}
      sx={{
        display: 'flex',
        p: 0.5,
        mb: 0.5,
        width: 'calc(100% - 8px)',
        borderRadius: 1,
        bgcolor: item.status === 'ready' ? (t) => `${t.palette.warning.main}20` : undefined,
      }}
      onClick={handleClick}
      data-testid="orderItem"
    >
      {!hideStatus && (
        <Box pt={1} pr={1} width={24} flexShrink={0}>
          <OrderItemStatus status={item.status} />
        </Box>
      )}

      <Box sx={{ mr: 1, height: 40 }}>
        <Image src={dish?.imageUrl} />
      </Box>
      <Box sx={{ overflow: 'hidden', flexGrow: 1 }}>
        <Typography sx={{ lineHeight: 1.1, mb: 0.5 }} noWrap={noWrap}>
          {dish?.name}
          {item.byWeight && ` ${item.weight}${t[item.unit || 'g'][lang]}`}
        </Typography>

        {item.variation && (
          <Typography
            variant="body2"
            sx={{ lineHeight: 1 }}
            color={(t) => (item.status === 'complete' ? t.palette.text.disabled : t.palette.text.secondary)}
          >
            {item.variation.name}
          </Typography>
        )}
        {item.modifications?.map((mod) => (
          <Typography key={mod.name} variant="body2" sx={{ lineHeight: 1 }} color={(t) => t.palette.text.secondary}>
            {mod.name}
          </Typography>
        ))}
        {item.comment && (
          <Typography variant="body2" sx={{ lineHeight: 1 }} color={(t) => t.palette.text.secondary}>
            {item.comment}
          </Typography>
        )}
      </Box>

      {rightContent}
    </Box>
  );
};

export default OrderItemRow;
